import { type RewardsData } from "@/hooks/useRewards";

interface TiersSectionProps {
  currentTier: RewardsData["tier"];
  currentPoints: number;
}

const TIERS = [
  {
    key: "bronze",
    label: "برونزي",
    range: "0 — 99 نقطة",
    icon: "ri-medal-line",
    color: "text-amber-700",
    bg: "bg-amber-700/10",
    border: "border-amber-700/30",
    glow: "shadow-amber-700/20",
    perks: ["وصول للعروض الحصرية", "نقطة لكل 10 ر.س", "تتبع الطلبات"],
  },
  {
    key: "silver",
    label: "فضي",
    range: "100 — 299 نقطة",
    icon: "ri-medal-2-line",
    color: "text-gray-300",
    bg: "bg-gray-300/10",
    border: "border-gray-300/30",
    glow: "shadow-gray-300/20",
    perks: ["كل مزايا البرونزي", "أولوية في الدعم", "إشعارات العروض مبكراً"],
  },
  {
    key: "gold",
    label: "ذهبي",
    range: "300 — 599 نقطة",
    icon: "ri-vip-crown-line",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    border: "border-amber-400/30",
    glow: "shadow-amber-400/20",
    perks: ["كل مزايا الفضي", "خصم 3% على كل طلب", "هدية عيد ميلاد"],
  },
  {
    key: "platinum",
    label: "بلاتيني",
    range: "600+ نقطة",
    icon: "ri-vip-diamond-line",
    color: "text-cyan-300",
    bg: "bg-cyan-300/10",
    border: "border-cyan-300/30",
    glow: "shadow-cyan-300/20",
    perks: ["كل مزايا الذهبي", "خصم 5% تلقائي", "شحن مجاني دائماً", "وصول VIP للمنتجات الجديدة"],
  },
] as const;

export default function TiersSection({ currentTier, currentPoints }: TiersSectionProps) {
  return (
    <div className="bg-[#111] border border-white/8 rounded-3xl p-6">
      <h2 className="text-white text-base font-black mb-5 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <i className="ri-vip-crown-2-line text-amber-400"></i>
        مستويات العضوية
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TIERS.map((tier) => {
          const isActive = currentTier === tier.key;
          const isPassed = TIERS.findIndex(t => t.key === currentTier) > TIERS.findIndex(t => t.key === tier.key);

          return (
            <div
              key={tier.key}
              className={`relative rounded-2xl p-4 border transition-all ${
                isActive
                  ? `${tier.bg} ${tier.border} ring-1 ${tier.border}`
                  : isPassed
                  ? `${tier.bg} ${tier.border} opacity-60`
                  : "bg-[#1a1a1a] border-white/5"
              }`}
            >
              {isActive && (
                <div className={`absolute -top-2 right-4 flex items-center gap-1 ${tier.bg} border ${tier.border} rounded-full px-2.5 py-0.5`}>
                  <i className={`ri-map-pin-2-fill ${tier.color} text-[10px]`}></i>
                  <span className={`${tier.color} text-[10px] font-black`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                    مستواك الحالي
                  </span>
                </div>
              )}
              {isPassed && (
                <div className="absolute -top-2 right-4 flex items-center gap-1 bg-green-400/10 border border-green-400/30 rounded-full px-2.5 py-0.5">
                  <i className="ri-check-line text-green-400 text-[10px]"></i>
                  <span className="text-green-400 text-[10px] font-black" style={{ fontFamily: "'Cairo', sans-serif" }}>
                    تم تجاوزه
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mb-3 mt-1">
                <div className={`w-10 h-10 flex items-center justify-center ${tier.bg} border ${tier.border} rounded-xl`}>
                  <i className={`${tier.icon} ${tier.color} text-lg`}></i>
                </div>
                <div>
                  <p className={`font-black text-sm ${isActive || isPassed ? tier.color : "text-gray-400"}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                    {tier.label}
                  </p>
                  <p className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>{tier.range}</p>
                </div>
              </div>

              <ul className="flex flex-col gap-1.5">
                {tier.perks.map((perk) => (
                  <li key={perk} className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-check-line text-xs ${isActive || isPassed ? tier.color : "text-gray-600"}`}></i>
                    </div>
                    <span className={`text-[11px] ${isActive || isPassed ? "text-gray-300" : "text-gray-600"}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                      {perk}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Progress indicator for current tier */}
              {isActive && (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <p className={`text-[11px] font-bold ${tier.color}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
                    رصيدك: {currentPoints.toLocaleString()} نقطة
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
